import { hasSorting, SortDirection, Sorting, WithSorting } from '~/types'
import type { GanttRowBarSwitching, GanttRowData } from '~/lib/gantt'
import { GanttColumnComputed } from '~/lib/gantt/columnComputed'
import { GanttColumnTable } from '~/lib/gantt/columnTable'
import { reactive } from 'vue'
import { getTextElementWidth } from '~/helper/browser'

export const ROW_MIXED_VALUES = '[...]'

export enum ColumnTypes {
  grouped = 'grouped',
  visual = 'visual',
  computed = 'computed',
}

/**
 * Preferred in sorting, used in API request, different color ...
 */
export function isPrimaryCol(col: ColumnBase<ColumnTypes>): col is GanttColumnTable & ColumnBase<ColumnTypes.grouped> {
  return col.type === ColumnTypes.grouped
}

export type ColumnStyle = {
  minWidth: number
  flexBasis: number
  flexGrow: 1 | 0 // the last col should grow ( == 1)
  flexShrink: 0
  order: number
}

/**
 * Rotates sorting (null -> Ascending -> Descending -> null)
 */
export function toggleSort(sortable: ColumnBase<ColumnTypes>) {
  if (sortable.sorting == null) {
    // important reactive() to track change on direction later
    sortable.sorting = reactive({
      direction: SortDirection.ASCENDING,
      order: Date.now(),
    })
  } else if (sortable.sorting.direction == SortDirection.ASCENDING) {
    sortable.sorting.direction = SortDirection.DESCENDING
  } else {
    sortable.sorting = null
  }
}

/**
 * Sets minWidth and flexBasis based on label and constants
 */
export function setColumnWidths(col: ColumnBase<ColumnTypes>, width?: number) {
  const PADDING = 18
  const SORT_BUTTON = 24 + 8

  col.style.minWidth = SORT_BUTTON + 8
  if (width == undefined) {
    const label = col.label(false)
    const textWidth = getTextElementWidth(label)
    col.style.flexBasis = Math.ceil(textWidth + PADDING + SORT_BUTTON)
  } else {
    col.style.flexBasis = width
  }
  return col.style
}

/**
 * Base interface a column (class) should implement
 */
export interface ColumnBase<T extends ColumnTypes> {
  id: string
  type: T
  sorting: Sorting | null
  // is not directly set as style -> See: useGanttColumns.ts: columnStyle
  style: ColumnStyle

  isDateColumn(): boolean
  toggleSort(): void
  label(_short: boolean): string

  rowValue(row: GanttRowData): string
  barValue(bar: GanttRowBarSwitching): string

  cssClass(header?: boolean): Record<string, boolean>
  cssStyle(last: boolean): Record<keyof ColumnStyle, any>

  equals(_other: ColumnBase<ColumnTypes>): boolean
  copy(): ColumnBase<T>
  toJSON(): Record<string, any> // should at least dump "type" to be able to be restored from localStorage
}

export type GanttColumn = GanttColumnTable | GanttColumnComputed
export type GanttColumnWithSorting = WithSorting<GanttColumn>
export function isColumnWithSorting(column: ColumnBase<ColumnTypes>): column is GanttColumnWithSorting {
  return hasSorting(column)
}

export type GanttColumnWithSortingMap = {
  primary: GanttColumnWithSorting[]
  secondary: GanttColumnWithSorting[]
}
export type ColumnMapped = {
  column: GanttColumn
  value: string
}
